import { useEffect, useRef, useState } from "react";
import { config } from "../config";
import {
  defaultThemeStyles,
  setThemeMode,
  themeInitialState,
} from "../constants";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import {
  generateViewConfigs,
  injectStyleVariables,
  type ColorPalette,
  type IViewConfig,
  type StylesConfig,
} from "lola-framework-ui-test";

const defaultThemeFileName = config.THEME;

type ThemeConfig = {
  font: typeof themeInitialState.font;
  colors: typeof themeInitialState.colors & {
    useSystemTheme?: boolean;
    lightness?: "light" | "dark";
  };
  styles?: StylesConfig;
};

const getSystemLightness = (): "light" | "dark" =>
  window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";

/** Legacy large paddings that used to ship next to buttonSize and blocked size mapping. */
const LEGACY_LARGE_BUTTON_PADDINGS = new Set(["20px", "1.5rem"]);

/**
 * Kit padding priority: explicit buttonPadding > buttonSize map > legacy 20px.
 * Older themes set both buttonSize and buttonPadding:"20px", so medium/small
 * never changed the visual size. Drop that legacy padding when size is set.
 */
const normalizeThemeStyles = (styles: StylesConfig): StylesConfig => {
  const next = { ...styles };
  if (
    next.buttonSize &&
    next.buttonPadding &&
    LEGACY_LARGE_BUTTON_PADDINGS.has(next.buttonPadding)
  ) {
    delete next.buttonPadding;
  }
  return next;
};

const FONT_META_KEYS = new Set(["fontfamily", "fontcdn"]);

/** Same clamp formulas as useLolaTheme / legacy useTheme. */
const DEFAULT_DYNAMIC_FORMULAS: Record<string, string> = {
  h1: "2vw + 0.25rem",
  highlight: "2vw + 0.25rem",
  h2: "1.5vw + 0.25rem",
  bodycopy: "1.5vw + 0.25rem",
  secondaryCta: "1.5vw + 0.25rem",
  mainButtonText: "1.5vw + 0.25rem",
  footerText: "0.85rem",
  step: "0.75vw + 0.25rem",
};

const camelToKebab = (str: string) =>
  str.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);

/**
 * Inject theme.font → CSS classes (.main-button-text, .h1, …).
 * Legacy useTheme did this in a useEffect; generateViewConfigs only handles colors.
 */
const injectThemeFontStyles = (font: ThemeConfig["font"]) => {
  const fontFamily = font.fontfamily || "sans-serif";
  const css = Object.entries(font)
    .filter(
      ([key, value]) =>
        // Kit Button always uses .main-button-text — inject like useLolaTheme.
        !FONT_META_KEYS.has(key) &&
        typeof value === "object" &&
        value !== null
    )
    .map(([className, rules]) => {
      const config = rules as {
        fontWeight?: string;
        min?: string;
        max?: string;
        lineHeight?: string;
        dynamicFormula?: string;
      };
      const dynamicFormula =
        config.dynamicFormula || DEFAULT_DYNAMIC_FORMULAS[className] || "1rem";
      const fontSize =
        config.min && config.max
          ? `font-size: clamp(${config.min}, ${dynamicFormula}, ${config.max});`
          : "";
      return [
        `.${camelToKebab(className)} {`,
        fontSize,
        `font-weight: ${config.fontWeight || "400"};`,
        `line-height: ${config.lineHeight || "normal"};`,
        `font-family: ${fontFamily}, sans-serif;`,
        `}`,
      ]
        .filter(Boolean)
        .join(" ");
    })
    .join("\n");

  // Keep both ids in sync (legacy useTheme + new useLolaTheme)
  for (const styleTagId of ["global-theme-styles", "lola-text-styles"]) {
    let styleTag = document.getElementById(styleTagId) as HTMLStyleElement | null;
    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.id = styleTagId;
      document.head.appendChild(styleTag);
    }
    styleTag.textContent = css;
  }
};

export const useReadTheme = () => {
  const { setTheme } = useStore();
  const { session } = useSessionStore();
  const [isLoadingTheme, setIsLoadingTheme] = useState(true);
  const [reedTheme, setReedTheme] = useState<ThemeConfig>(themeInitialState);
  const hasPartnerThemeRef = useRef(false);
  const latestThemeRef = useRef<ThemeConfig>(themeInitialState);

  // Apply a theme (partner or default) via the kit's generateViewConfigs.
  // - color mappings (buttons, aura) follow theme.colors.lightness
  // - with useSystemTheme, AuraLayout keeps themeType on brand lightness and
  //   syncs html.light/dark to the OS so white-view-background uses CSS vars
  const applyTheme = (json: ThemeConfig) => {
    const styles = normalizeThemeStyles(json.styles ?? defaultThemeStyles);
    const normalized: ThemeConfig = { ...json, styles };
    latestThemeRef.current = normalized;
    setReedTheme(normalized);
    injectThemeFontStyles(normalized.font);

    const useSystemTheme = json.colors?.useSystemTheme === true;
    const themeLightness: "light" | "dark" =
      json.colors?.lightness === "dark" ? "dark" : "light";
    const systemLightness = getSystemLightness();

    // Keep theme.lightness for brand mappings (buttons). Do not feed OS into it.
    const colorsForViews = {
      ...json.colors,
      lightness: themeLightness,
      useSystemTheme,
    } as ColorPalette;

    injectStyleVariables(styles);
    // Kit owns view chrome:
    // - themeType stays on colors.lightness (NOT the OS)
    // - AuraLayout adds white-view-background when useSystemTheme + themeType dark
    // - auraColors stay brand gradients; html.light/dark follows OS for CSS vars
    const views = generateViewConfigs(colorsForViews, styles);

    setTheme(views as IViewConfig);

    // DOM semantic tokens: OS when useSystemTheme, else theme lightness.
    // Do NOT rewrite view themeType to the OS — that kills the teal aura in light mode.
    setThemeMode(useSystemTheme ? systemLightness : themeLightness);
    setIsLoadingTheme(false);
  };

  // 1) On mount: load default theme ONLY if no partner theme is available
  useEffect(() => {
    const partnerTheme = useSessionStore.getState().session.data?.partner?.theme;

    if (partnerTheme && typeof partnerTheme === "object") {
      hasPartnerThemeRef.current = true;
      applyTheme(partnerTheme as ThemeConfig);
      return;
    }

    (async () => {
      try {
        const res = await fetch(`/${defaultThemeFileName}`);
        const json = await res.json();
        if (!hasPartnerThemeRef.current) {
          applyTheme(json as ThemeConfig);
        }
      } catch (err) {
        console.error("Error loading default theme:", err);
        if (!hasPartnerThemeRef.current) {
          applyTheme(themeInitialState);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2) When session gets a partner theme (from claim), override whatever is loaded
  useEffect(() => {
    const partnerTheme = session.data?.partner?.theme;
    if (!partnerTheme || typeof partnerTheme !== "object") return;

    hasPartnerThemeRef.current = true;
    applyTheme(partnerTheme as ThemeConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.data?.partner?.theme]);

  // 3) Re-apply when OS theme changes and partner/theme opted into system theme
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;

    const onChange = () => {
      const current = latestThemeRef.current;
      if (current.colors?.useSystemTheme === true) {
        applyTheme(current);
      }
    };

    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isLoadingTheme, reedTheme };
};
